// "More like this" as a Browse search, seeded from an artwork id in the URL.
//
// The View modal's "Open as a search" button opens `/search?like=<artworkId>` in a new tab.
// A fresh tab cannot carry the query vector — it is ~6 kB — and it cannot carry a data: URL
// preview either, so the id is the whole payload and everything else is looked up here.
//
// The lookup is `search.vector` with `seedId`, which is the SAME endpoint the file-upload
// button uses. It already accepts a seed artwork (that is how /labs "more like this" works)
// and already returns the combined query vector under `returnVec`. So an artwork-seeded
// search and an upload-seeded one converge on one store entry and one `search.load` body:
// paging, filters and the sidebar chip need no idea which produced it.
import { api } from "../../api";
import { getAction } from "../../actionRegistry";
import { setSearchActive } from "../../globalState";
import { toastError } from "../../ui/messages";
import { vecToB64 } from "./downscale";
import { setSearchImage } from "./store";

/** The query parameter the View modal links with. */
export const SEED_PARAM = "like";

/** The artwork id in `?like=`, or null when absent or not a positive integer. */
export function seedArtworkIdFromUrl(search: string): number | null {
  const raw = new URLSearchParams(search).get(SEED_PARAM);
  if (raw === null) return null;
  // Strict: `Number("12abc")` is NaN but `parseInt` would happily return 12, and a sloppy
  // id here would silently search for the wrong artwork.
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * If the URL names a seed artwork, fetch its embedding and run the search.
 *
 * Errors are reported and swallowed: an artwork with no embedding, or a vector store that
 * is briefly unavailable, must leave a usable Browse page rather than a broken one.
 */
export async function applySeedFromUrl(): Promise<void> {
  const id = seedArtworkIdFromUrl(window.location.search);
  if (id === null) return;

  try {
    // `k: 1` because the ranked results are thrown away — only `queryVec` is wanted, the
    // same reason the upload button passes it.
    const res = await api.search.vector({ seedId: id, k: 1, returnVec: true });
    if (!res.queryVec) throw new Error("That artwork cannot be used for an image search");

    setSearchImage({
      name: `Artwork #${String(id)}`,
      preview: "",
      vecB64: vecToB64(res.queryVec),
      seedArtworkId: id,
    });
    // `search`, not `searchtimed`.
    //
    // `search()`'s empty-box branch only runs when `search_active === 1`, and the timed
    // variant waits a second before checking. Setting the flag here and letting a debounce
    // read it a second later meant page initialisation could reset it in between: the
    // search silently never ran, the chip appeared, and the grid stayed on the default
    // listing — which looked exactly like a working feature returning bad results.
    //
    // Calling straight through closes that window. There is nothing to debounce anyway:
    // this fires once, from a URL, not from typing.
    setSearchActive(1);
    getAction("search")?.();
  } catch (err: unknown) {
    toastError(
      err instanceof Error ? err.message : "Could not search for artworks like that one"
    );
  }
}
